import { ReactNode } from 'react';

import { ActionIcon, Box, CopyButton, Group, HoverCard, Stack, Text, Tooltip } from '@mantine/core';
import { IconCheck, IconCopy } from '@tabler/icons-react';

import { deviceHoverContent, DeviceHoverSource } from './deviceHover';

/** Device name with the transmission-tracking hover: same rows, highlight, and id copy. */
export function DeviceHoverCard({
  device,
  label
}: {
  device: DeviceHoverSource | null;
  label?: ReactNode;
}) {
  const content = deviceHoverContent(device);
  const title = typeof label === 'string' ? label : content.title;

  return (
    <HoverCard
      width={280}
      shadow="md"
      position="bottom-start"
      offset={4}
      openDelay={150}
      closeDelay={100}
      withArrow
      middlewares={{ flip: true, shift: false }}>
      <HoverCard.Target>
        <Box style={{ cursor: 'help', width: 'fit-content', maxWidth: '100%' }}>
          {typeof label === 'string' || label == null ? (
            <Text
              size="sm"
              fw={600}
              c="plum.7"
              style={{ textDecoration: 'underline dotted', textUnderlineOffset: 3 }}>
              {title}
            </Text>
          ) : (
            label
          )}
        </Box>
      </HoverCard.Target>
      <HoverCard.Dropdown>
        <Stack gap={6}>
          <Text size="sm" fw={700}>
            {content.title}
          </Text>
          {content.rows.map((row) => (
            <Group
              key={row.label}
              gap={8}
              wrap="nowrap"
              align="flex-start"
              px={row.highlight ? 6 : 0}
              py={row.highlight ? 4 : 0}
              style={
                row.highlight
                  ? { background: 'var(--mantine-color-orange-1)', borderRadius: 4 }
                  : undefined
              }>
              <Text
                size="xs"
                c={row.highlight ? 'orange.9' : 'dimmed'}
                fw={row.highlight ? 700 : 400}
                style={{ width: 118, flexShrink: 0 }}>
                {row.label}
              </Text>
              <Text
                size="xs"
                c={row.highlight ? 'orange.9' : undefined}
                fw={row.highlight ? 700 : 400}
                style={{ flex: 1, minWidth: 0, wordBreak: 'break-all' }}>
                {row.value}
              </Text>
            </Group>
          ))}
          {content.id && (
            <Group gap={6} wrap="nowrap" align="center" mt={4}>
              <Text
                size="xs"
                c="dimmed"
                ff="monospace"
                style={{ flex: 1, minWidth: 0, wordBreak: 'break-all' }}>
                {content.id}
              </Text>
              <CopyButton value={content.id} timeout={1500}>
                {({ copied, copy }) => (
                  <Tooltip label={copied ? 'Copied' : 'Copy id'} withArrow>
                    <ActionIcon
                      variant="subtle"
                      color={copied ? 'teal' : 'gray'}
                      size="sm"
                      aria-label="Copy device id"
                      onClick={copy}>
                      {copied ? <IconCheck size={14} /> : <IconCopy size={14} />}
                    </ActionIcon>
                  </Tooltip>
                )}
              </CopyButton>
            </Group>
          )}
        </Stack>
      </HoverCard.Dropdown>
    </HoverCard>
  );
}
